<template>
  <div class="rewards-container" :class="{ dark: dark }">
    <!-- Position headline -->
    <div class="position-block">
      <div class="position-eyebrow">You're on the list</div>
      <div class="position-row">
        <span class="position-label">Your position: </span>
        <span class="position-number">#{{ animatedPriority }}</span>
      </div>
      <p class="position-sub">
        Refer 5 friends and I'll bump you to the top of the waitlist
      </p>
    </div>

    <!-- Referral link -->
    <div class="ref-box">
      <div class="ref-link" @click="copyLink">
        <span class="ref-link-text">{{ displayLink }}</span>
      </div>
      <div class="ref-actions">
        <button type="button" @click="copyLink" class="action-btn copy-btn">
          <svg
            v-if="!copied"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
          <svg
            v-else
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>{{ copied ? 'Copied!' : 'Copy link' }}</span>
        </button>
        <button type="button" @click="shareLink" class="action-btn share-btn">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          <span>Share</span>
        </button>
      </div>

      <!-- Quick share intents -->
      <div class="quick-share">
        <a
          :href="smsHref"
          class="qs-btn"
          aria-label="Share via iMessage"
          @click="trackQuick('sms')"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
            />
          </svg>
          iMessage
        </a>
        <a
          :href="whatsappHref"
          target="_blank"
          rel="noopener"
          class="qs-btn"
          aria-label="Share on WhatsApp"
          @click="trackQuick('whatsapp')"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.768.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
            />
          </svg>
          WhatsApp
        </a>
        <a
          :href="twitterHref"
          target="_blank"
          rel="noopener"
          class="qs-btn"
          aria-label="Share on X"
          @click="trackQuick('twitter')"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
            />
          </svg>
          X
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import * as amplitude from '@amplitude/analytics-browser';

export default {
  name: 'WaitlistRewards',
  props: {
    refLink: { type: String, required: true },
    currPriority: { type: [Number, String], default: 0 },
    panel: { type: String, default: 'top' },
    dark: { type: Boolean, default: false },
  },
  setup(props) {
    const copied = ref(false);
    const animatedPriority = ref(0);

    const displayLink = computed(() => {
      if (!props.refLink) return `${window.location.origin}/?ref_id=`;
      return props.refLink.replace(/^https?:\/\//, '');
    });

    const shareText =
      'I just joined Tilly! It helps you make plans and actually go. Use my link to get ahead on the waitlist. ';

    const smsHref = computed(
      () => `sms:&body=${encodeURIComponent(`${shareText} ${props.refLink}`)}`,
    );
    const whatsappHref = computed(
      () =>
        `https://wa.me/?text=${encodeURIComponent(
          `${shareText} ${props.refLink}`,
        )}`,
    );
    const twitterHref = computed(
      () =>
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          shareText,
        )}&url=${encodeURIComponent(props.refLink || '')}`,
    );

    const copyLink = async () => {
      try {
        await navigator.clipboard.writeText(props.refLink);
        copied.value = true;
        amplitude.track('Referral Link Copied', { panel: props.panel });
        setTimeout(() => {
          copied.value = false;
        }, 2000);
      } catch (e) {
        // ignore
      }
    };

    const shareLink = async () => {
      amplitude.track('Referral Shared', { panel: props.panel });
      const shareData = {
        title: 'Tilly waitlist',
        text: shareText,
        url: props.refLink,
      };
      if (navigator.share) {
        try {
          await navigator.share(shareData);
        } catch (err) {
          if (err.name !== 'AbortError') {
            // fall through silently
          }
        }
      } else {
        await copyLink();
      }
    };

    const trackQuick = (channel) => {
      amplitude.track('Referral Shared', {
        panel: props.panel,
        channel,
      });
    };

    // Animate priority count-up
    const animateTo = (target) => {
      const start = animatedPriority.value;
      const duration = 1200;
      const startedAt = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - startedAt) / duration);
        const eased = 1 - (1 - t) ** 3;
        animatedPriority.value = Math.floor(start + (target - start) * eased);
        if (t < 1) requestAnimationFrame(tick);
        else animatedPriority.value = target;
      };
      requestAnimationFrame(tick);
    };

    onMounted(() => {
      const target = parseInt(props.currPriority, 10) || 0;
      if (target > 0) animateTo(target);
    });

    watch(
      () => props.currPriority,
      (val) => {
        const target = parseInt(val, 10) || 0;
        if (target > 0) animateTo(target);
      },
    );

    return {
      copied,
      animatedPriority,
      displayLink,
      smsHref,
      whatsappHref,
      twitterHref,
      copyLink,
      shareLink,
      trackQuick,
    };
  },
};
</script>

<style scoped>
.rewards-container {
  width: 100%;
  max-width: 540px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  animation: fadeUp 0.9s cubic-bezier(0.22, 0.61, 0.36, 1);
}

@keyframes fadeUp {
  0% {
    opacity: 0;
    transform: translateY(24px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== Position block ===== */
.position-block {
  text-align: center;
}

.position-eyebrow {
  font-family: 'AirbnbCereal_W_Md', sans-serif;
  font-size: 0.75rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.65);
}
.rewards-container:not(.dark) .position-eyebrow {
  color: #58a8e1;
}

.position-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.6rem;
  margin-bottom: 0.6rem;
}

.position-label {
  font-size: 1.1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}
.rewards-container:not(.dark) .position-label {
  color: #4b5563;
}

.position-number {
  font-family: 'AirbnbCereal_W_Bd', sans-serif;
  font-size: 3.25rem;
  font-weight: 800;
  background: linear-gradient(135deg, #fdad00 0%, #f4846c 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  letter-spacing: -0.03em;
  line-height: 1;
}

.position-sub {
  font-size: 0.92rem;
  line-height: 1.5;
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
}
.rewards-container:not(.dark) .position-sub {
  color: #6b7280;
}

/* ===== Ref box ===== */
.ref-box {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 1rem;
}
.rewards-container:not(.dark) .ref-box {
  background: white;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.ref-link {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 0.85rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 0.75rem;
}
.rewards-container:not(.dark) .ref-link {
  background: #f9fafb;
  border-color: #e5e7eb;
}
.ref-link:hover {
  border-color: rgba(88, 168, 225, 0.5);
}

.ref-link-text {
  font-family: 'AirbnbCereal_W_Md', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: #58a8e1;
  word-break: break-all;
  display: block;
}
.rewards-container.dark .ref-link-text {
  color: rgba(255, 255, 255, 0.96);
}

.ref-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border: 1px solid transparent;
  border-radius: 12px;
  font-family: 'AirbnbCereal_W_Md', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.25s ease, background 0.25s ease;
  min-height: 48px;
}
.action-btn svg {
  flex-shrink: 0;
}
.action-btn:focus-visible {
  outline: 2px solid rgba(88, 168, 225, 0.45);
  outline-offset: 2px;
}
.copy-btn {
  background: #58a8e1;
  color: white;
  box-shadow: 0 6px 16px rgba(88, 168, 225, 0.35);
}
.copy-btn:hover {
  background: #4a90c9;
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(88, 168, 225, 0.45);
}
.share-btn {
  background: rgba(255, 255, 255, 0.12);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.18);
}
.rewards-container:not(.dark) .share-btn {
  background: #f3f4f6;
  color: #1f2937;
  border-color: #e5e7eb;
}
.share-btn:hover {
  transform: translateY(-2px);
  background: rgba(255, 255, 255, 0.18);
}
.rewards-container:not(.dark) .share-btn:hover {
  background: #e5e7eb;
}

.quick-share {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
}

.qs-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.85rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.2s ease;
  min-height: 36px;
}
.rewards-container:not(.dark) .qs-btn {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #374151;
}
.qs-btn:hover {
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 0.14);
}
.rewards-container:not(.dark) .qs-btn:hover {
  background: #f3f4f6;
}

@media (max-width: 480px) {
  .position-number {
    font-size: 2.6rem;
  }
  .ref-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.45rem;
  }
  .action-btn {
    padding: 0.8rem 0.75rem;
    font-size: 0.9rem;
  }
}
</style>
